import { useState } from 'react'
import { catalog as catalogApi, leases as leaseApi } from '@/api/endpoints'
import type { LeaseChangeApproval } from '@/api/types'
import { Choice } from '@/components/Choice'
import { Err } from '@/components/Err'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { usePoll } from '@/hooks/usePoll'
import { gpuMemoryFloorGb } from '@/domain/flavor'
import { CHANGE_REASONS, MAX_GPUS, changeLines, GPU_NONE } from '@/domain/lease'
import { DASH, size, sizeMb } from '@/ui/format'
import { ChangeLines, Note, NowTo, SpecCard } from './SpecCards'

// 변경을 허락하는 자리(B-35). 신청한 숫자가 채워져 있고 최고 관리자가 그 자리에서 고쳐 승인한다 —
// 숫자 하나 때문에 반려하고 처음부터 다시 받게 하지 않는다. 고치지 않고 누르면 신청한 그대로 간다.
// 본문은 변경 신청 창과 같은 카드다(`./SpecCards.tsx`): `지금` 은 VM 의 지금 값, 칸은 허락할 값이다.
//
// 모델은 신청한 사람의 것이라 여기서 고르지 않는다. 그래서 **장 수 칸은 신청이 모델을 말할 때만 선다** —
// 모델 없는 변경에서 장 수를 올리면 받을 하드웨어가 정해지지 않아 백엔드가 거절한다.
//
// 허락하는 숫자는 VM 이 있는 노드에 남은 만큼이다(`leases.room`) — 변경은 그 노드에서만 자원을 받으므로
// 넘는 승인은 백엔드가 409 로 거절한다. 노드를 못 읽으면 범위는 신청한 데까지이고 승인을 막지 않는다.
//
// 카드가 요구하는 메모리는 VM 생성 페이지와 같은 함수로 재서 여기서도 막는다. 승인은 하한 아래를
// 올려 주지 않고 거절하므로(`store.ApproveLeaseChange`), 재지 않으면 승인자는 몇 GB 가
// 필요한지 모른 채 숫자를 고치고 거절문을 읽는다. 한 장의 메모리는 그 노드의 것(`leases.room`)을 먼저,
// 없으면 `/catalog` 의 모델 항목을 읽는다.

const CATALOG_POLL_MS = 60000
const ROOM_POLL_MS = 15000

export type ApprovableChange = {
  target_cores: number
  target_memory_mb: number
  target_gpus?: number | null
  gpu_model?: string | null
}

/** 그 VM 의 지금. */
export type ChangeNow = {
  cores?: number | undefined
  memory_mb?: number | undefined
  gpus: number
  gpu_model?: string | null | undefined
}

export function ChangeApproveDialog({
  open,
  title,
  leaseId,
  change,
  now,
  onClose,
  approve,
  onDone,
}: {
  open: boolean
  title: string
  leaseId: string
  change: ApprovableChange
  now: ChangeNow
  onClose: () => void
  approve: (body: LeaseChangeApproval) => Promise<unknown>
  onDone: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => (v ? undefined : onClose())}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {open ? <ApproveForm leaseId={leaseId} change={change} now={now} onClose={onClose} approve={approve} onDone={onDone} /> : null}
      </DialogContent>
    </Dialog>
  )
}

function ApproveForm({
  leaseId,
  change,
  now,
  onClose,
  approve,
  onDone,
}: {
  leaseId: string
  change: ApprovableChange
  now: ChangeNow
  onClose: () => void
  approve: (body: LeaseChangeApproval) => Promise<unknown>
  onDone: () => void
}) {
  const askedCores = String(change.target_cores)
  const askedMemoryGb = String(Math.ceil(change.target_memory_mb / 1024))
  const askedGpus = change.target_gpus
  const [cores, setCores] = useState(askedCores)
  const [memoryGb, setMemoryGb] = useState(askedMemoryGb)
  const [gpus, setGpus] = useState(askedGpus ?? 0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  // 한 장의 메모리와 노드의 남음만 쓴다. 둘 다 못 읽으면 하한도 범위도 서지 않고 창은 신청한 그대로를 허락한다 —
  // 그 읽기의 실패로 승인을 막으면 고칠 곳 없는 막힘이 된다.
  const catalog = usePoll(() => catalogApi.get(), CATALOG_POLL_MS)
  const room = usePoll(() => leaseApi.room(leaseId), ROOM_POLL_MS)
  const r = room.data

  const cards = askedGpus != null && change.gpu_model ? change.gpu_model : null
  const onNode = r?.gpus.find((g) => g.model === change.gpu_model)
  const maxCards = Math.min(MAX_GPUS, onNode ? onNode.held + onNode.free : (askedGpus ?? 0))
  // 빠뜨린 값은 신청한 그대로 간다 — 고친 숫자만 싣는다.
  const body: LeaseChangeApproval = {}
  if (cores !== '' && cores !== askedCores) body.cores = Number(cores)
  if (memoryGb !== '' && memoryGb !== askedMemoryGb) body.memory_mb = Number(memoryGb) * 1024
  if (askedGpus != null && gpus !== askedGpus) body.gpus = gpus
  // 허락하는 장 수가 요구하는 메모리. 비운 칸은 신청한 숫자가 그대로 가므로 그 숫자도 함께 잰다 —
  // 장 수를 올리면 신청 때 통과한 메모리가 새 하한 아래일 수 있다.
  const vramMb = onNode?.vram_mb ?? (catalog.data?.gpus ?? []).find((g) => g.model === change.gpu_model)?.vram_mb ?? 0
  const targetGpus = askedGpus != null ? gpus : now.gpus
  const floorGb = gpuMemoryFloorGb(vramMb, askedGpus != null ? gpus : 0)
  const allowedGb = Number(memoryGb === '' ? askedMemoryGb : memoryGb)
  const memoryBad = floorGb > 0 && allowedGb < floorGb
  const allowedCores = Number(cores === '' ? askedCores : cores)

  // 그 노드에 남은 만큼까지다. VM 의 지금 값을 모르거나 노드를 재지 못하면 막지 않는다.
  const maxCores = r?.cores != null && now.cores != null ? now.cores + r.cores : null
  const maxMemoryGb = r?.memory_mb != null && now.memory_mb != null ? Math.floor((now.memory_mb + r.memory_mb) / 1024) : null
  const coresBad = maxCores !== null && now.cores != null && allowedCores > Math.max(maxCores, now.cores)
  const memoryOver = maxMemoryGb !== null && now.memory_mb != null && allowedGb > Math.max(maxMemoryGb, Math.ceil(now.memory_mb / 1024))
  const lines =
    now.cores != null && now.memory_mb != null
      ? changeLines(
          { cores: now.cores, memory_mb: now.memory_mb, gpus: now.gpus, gpu_model: now.gpu_model },
          { cores: allowedCores, memory_mb: allowedGb * 1024, gpus: targetGpus, gpu_model: targetGpus > 0 ? (change.gpu_model ?? now.gpu_model) : null },
        )
      : []

  const submit = async () => {
    if (memoryBad || coresBad || memoryOver) return
    setBusy(true)
    setError(null)
    try {
      await approve(body)
      onDone()
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }
  const countItems = Array.from({ length: Math.max(maxCards, askedGpus ?? 0, now.gpus) + 1 }, (_, n) => ({
    value: String(n),
    title: n === 0 ? GPU_NONE : `${n}장`,
    disabled: n > maxCards && n !== askedGpus,
  }))
  return (
    <>
      <div className="grid gap-3">
        <SpecCard title="CPU" aside={maxCores !== null ? `최대 ${maxCores}코어` : undefined}>
          <NowTo
            id="change-approve-cores"
            label="코어"
            now={now.cores != null ? `${now.cores}코어` : DASH}
            unit="코어"
            value={cores}
            placeholder={askedCores}
            busy={busy}
            bad={coresBad}
            onChange={setCores}
          />
          {coresBad ? <Note bad>이 노드에 남은 코어는 {r?.cores}코어입니다.</Note> : null}
        </SpecCard>
        <SpecCard title="메모리" aside={maxMemoryGb !== null ? `최대 ${size(maxMemoryGb)}` : undefined}>
          <NowTo
            id="change-approve-memory"
            label="메모리 (GB)"
            now={now.memory_mb != null ? sizeMb(now.memory_mb) : DASH}
            unit="GB"
            value={memoryGb}
            placeholder={askedMemoryGb}
            busy={busy}
            bad={memoryBad || memoryOver}
            onChange={setMemoryGb}
          />
          {floorGb > 0 ? <Note bad={memoryBad}>{floorGb}GB 이상</Note> : null}
          {memoryOver ? <Note bad>이 노드에 남은 메모리는 {sizeMb(r?.memory_mb)}입니다.</Note> : null}
        </SpecCard>
        {cards ? (
          <SpecCard title={`GPU ${cards}`} aside={onNode ? `최대 ${onNode.held + onNode.free}장` : undefined}>
            <div className="grid gap-1.5">
              <Label htmlFor="change-approve-gpus">장 수</Label>
              <Choice id="change-approve-gpus" compact value={String(gpus)} items={countItems} disabled={busy} onChange={(v) => setGpus(Number(v))} />
            </div>
          </SpecCard>
        ) : null}
        {lines.length > 0 ? <ChangeLines lines={lines} /> : null}
      </div>
      <Err error={error} reasons={CHANGE_REASONS} />
      <DialogFooter>
        <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
          취소
        </Button>
        <Button type="button" disabled={busy || memoryBad || coresBad || memoryOver} onClick={() => void submit()}>
          {busy ? '승인 중' : '승인'}
        </Button>
      </DialogFooter>
    </>
  )
}
